import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, Trophy, BookOpen, Target, TrendingUp, Loader2, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';

const API = `${window.location.origin}/api`;

const ProgressPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [progressList, setProgressList] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [progressRes, coursesRes] = await Promise.all([
        axios.get(`${API}/progress`, { withCredentials: true }),
        axios.get(`${API}/courses`, { withCredentials: true })
      ]);

      setProgressList(progressRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error('Failed to load progress data');
    } finally {
      setLoading(false);
    }
  };

  const getCourseById = (courseId) => {
    return courses.find(c => c.id === courseId);
  };

  const getComplexityColor = (complexity) => {
    const colors = {
      novice: 'text-green-600 bg-green-100',
      proficient: 'text-amber-600 bg-amber-100',
      scholar: 'text-purple-600 bg-purple-100'
    };
    return colors[complexity] || colors.novice;
  };

  const calculateStats = () => {
    const total = progressList.length;
    const completed = progressList.filter(p => p.completion_percentage >= 100).length;
    const inProgress = progressList.filter(p => p.completion_percentage > 0 && p.completion_percentage < 100).length;
    const avgCompletion = total > 0 
      ? progressList.reduce((sum, p) => sum + p.completion_percentage, 0) / total
      : 0;

    return { total, completed, inProgress, avgCompletion };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center theme-bg">
        <Loader2 className="w-12 h-12 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg">
      {/* Header */}
      <header className={`${isDarkMode ? 'bg-black/90 border-amber-900/50' : 'bg-white/80 border-amber-200'} backdrop-blur-sm border-b shadow-sm`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
                data-testid="back-to-dashboard-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-2">
                <Trophy className={`w-6 h-6 ${isDarkMode ? 'text-amber-500' : 'text-amber-700'}`} />
                <span className={`text-xl font-bold ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>My Progress</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/profile')}
              className={`${isDarkMode ? 'text-amber-400' : 'text-amber-700 hover:bg-amber-100'} btn-glow rounded-lg`}
              title="Profile & Settings"
              data-testid="settings-btn"
            >
              <Settings className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="card-glow border-amber-200 bg-white/90 backdrop-blur-sm" data-testid="stats-total-enrolled">
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-2 mb-2">
                <BookOpen className="w-5 h-5 text-amber-600" />
                <CardDescription className="text-amber-700">Total Enrolled</CardDescription>
              </div>
              <CardTitle className="text-4xl text-amber-900">{stats.total}</CardTitle>
            </CardHeader>
          </Card>

          <Card className="card-glow border-green-200 bg-white/90 backdrop-blur-sm" data-testid="stats-completed-courses">
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-2 mb-2">
                <Trophy className="w-5 h-5 text-green-600" />
                <CardDescription className="text-green-700">Completed</CardDescription>
              </div>
              <CardTitle className="text-4xl text-green-900">{stats.completed}</CardTitle>
            </CardHeader>
          </Card>

          <Card className="card-glow border-orange-200 bg-white/90 backdrop-blur-sm" data-testid="stats-in-progress-courses">
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-2 mb-2">
                <Target className="w-5 h-5 text-orange-600" />
                <CardDescription className="text-orange-700">In Progress</CardDescription>
              </div>
              <CardTitle className="text-4xl text-orange-900">{stats.inProgress}</CardTitle>
            </CardHeader>
          </Card>

          <Card className="card-glow border-purple-200 bg-white/90 backdrop-blur-sm" data-testid="stats-avg-completion">
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-2 mb-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <CardDescription className="text-purple-700">Avg Completion</CardDescription>
              </div>
              <CardTitle className="text-4xl text-purple-900">{stats.avgCompletion.toFixed(0)}%</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Progress List */}
        <Card className="card-glow border-amber-200">
          <CardHeader>
            <CardTitle className={`text-2xl ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>Course Progress Details</CardTitle>
            <CardDescription className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>Track your learning journey across all courses</CardDescription>
          </CardHeader>
          <CardContent>
            {progressList.length === 0 ? (
              <div className="text-center py-12" data-testid="no-progress-message">
                <BookOpen className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-amber-100' : 'text-amber-900'}`}>No courses started yet</h3>
                <p className={`mb-6 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Start a course to track your progress</p>
                <Button
                  onClick={() => navigate('/courses')}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="browse-courses-btn"
                >
                  Browse Courses
                </Button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="progress-list">
                {progressList.map((progress) => {
                  const course = getCourseById(progress.course_id);
                  if (!course) return null;

                  return (
                    <Card
                      key={progress.id}
                      className={`border-amber-200 cursor-pointer card-hover card-glow ${isDarkMode ? 'bg-white/90' : 'bg-amber-50/50'}`}
                      onClick={() => navigate(`/course/${course.id}`)}
                      data-testid={`progress-card-${course.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
                          <div className="flex-1 mb-4 sm:mb-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${getComplexityColor(course.complexity)}`}>
                                {course.complexity.charAt(0).toUpperCase() + course.complexity.slice(1)}
                              </span>
                              {progress.completion_percentage >= 100 && (
                                <span className="px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-semibold">
                                  ✓ Completed
                                </span>
                              )}
                            </div>
                            <h3 className="text-lg font-bold mb-1 text-amber-900">{course.title}</h3>
                            <p className="text-sm text-amber-700">
                              {progress.completed_lessons?.length || 0} of {course.lessons.length} lessons completed
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-3xl font-bold mb-1 text-amber-900">
                              {progress.completion_percentage.toFixed(0)}%
                            </div>
                            <p className="text-xs text-amber-600">
                              {progress.completion_percentage >= 100 ? 'Completed' : 'In Progress'}
                            </p>
                          </div>
                        </div>
                        <Progress value={progress.completion_percentage} className="h-3" />
                        <div className="flex justify-between mt-3 text-xs text-amber-600">
                          <span>Started {new Date(progress.started_at).toLocaleDateString()}</span>
                          {progress.completed_at && (
                            <span>Finished {new Date(progress.completed_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProgressPage;
